import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard, Truck, Factory, Wrench, AlertTriangle,
  Package, FileBarChart, Users, LogOut, Menu, X, ChevronRight, Settings,
  BarChart2, AlertCircle, Leaf, UserCog, ShieldCheck, Monitor, FileText, Cpu
} from 'lucide-react';

const navGroups = [
  {
    label: null,
    items: [
      { path: '/cuadro-mandos', label: 'Cuadro de Mandos', icon: Monitor },
    ],
  },
  {
    label: 'MANTENIMIENTO',
    items: [
      { path: '/', label: 'Dashboard GMAO', icon: LayoutDashboard },
      { path: '/maquinaria-movil', label: 'Maq. Móvil', icon: Truck },
      { path: '/maquinaria-fija', label: 'Maq. Fija', icon: Factory },
      { path: '/ordenes-trabajo', label: 'Órdenes de Trabajo', icon: Wrench },
      { path: '/mantenimiento', label: 'Plan Mantenimiento', icon: Settings },
      { path: '/alertas', label: 'Alertas', icon: AlertTriangle, badge: '!' },
    ],
  },
  {
    label: 'OPERACIONES',
    items: [
      { path: '/operaciones', label: 'Producción y Pesaje', icon: BarChart2 },
      { path: '/incidencias', label: 'Incidencias', icon: AlertCircle },
      { path: '/control-ambiental', label: 'Control Ambiental', icon: Leaf },
    ],
  },
  {
    label: 'GESTIÓN',
    items: [
      { path: '/personal', label: 'Personal', icon: UserCog },
      { path: '/inventario', label: 'Almacén', icon: Package },
      { path: '/accesos', label: 'Control Accesos', icon: ShieldCheck },
      { path: '/documentos', label: 'Documentos', icon: FileText },
      { path: '/scada', label: 'Monitor SCADA', icon: Cpu },
    ],
  },
  {
    label: 'INFORMES',
    items: [
      { path: '/informes', label: 'Informes', icon: FileBarChart },
    ],
  },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleToggle = () => {
    if (window.innerWidth < 768) setMobileOpen(false);
    else setCollapsed(!collapsed);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Sidebar */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setMobileOpen(false)}
          data-testid="sidebar-backdrop"
        />
      )}
      <aside
        className={`fixed inset-y-0 left-0 z-40 md:static md:translate-x-0 flex flex-col bg-[#111827] text-white transition-all duration-300 ${collapsed ? 'md:w-16' : 'md:w-64'} w-64 flex-shrink-0 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}
        style={{ background: 'linear-gradient(180deg, #111827 0%, #0a0f1a 100%)' }}
      >
        {/* Logo */}
        <div className={`flex items-center border-b border-gray-800 ${collapsed ? 'justify-center p-3' : 'px-4 py-3'}`}>
          {!collapsed && (
            <div className="flex items-center gap-2 flex-1">
              <img
                src="https://customer-assets.emergentagent.com/job_6d55a2c9-77b0-47cd-9fb3-a9a0e5fd0f87/artifacts/50vp5qrw_VALORIZA_color_negativo%206.png"
                alt="Valoriza"
                className="h-8 object-contain"
              />
            </div>
          )}
          <button
            onClick={handleToggle}
            className="text-gray-400 hover:text-white transition-colors p-1 rounded"
            data-testid="sidebar-toggle"
          >
            {collapsed ? <Menu size={18} /> : <X size={18} />}
          </button>
        </div>

        {/* Subtitle */}
        {!collapsed && (
          <div className="px-4 py-2 border-b border-gray-800">
            <p className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold">GMAO</p>
            <p className="text-xs text-gray-300 font-medium">Ecoparque de Cáceres</p>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 py-2 overflow-y-auto">
          {navGroups.map((group) => (
            <div key={group.label || 'top'}>
              {group.label && !collapsed && (
                <div className="px-4 pt-4 pb-1">
                  <p className="text-[9px] text-gray-500 uppercase tracking-widest font-semibold">{group.label}</p>
                </div>
              )}
              {group.label && collapsed && <div className="h-px bg-gray-800 mx-3 my-2" />}
              {group.items.map((item) => {
                const Icon = item.icon;
                const isCuadro = item.path === '/cuadro-mandos';
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end={item.path === '/'}
                    className={({ isActive }) =>
                      `sidebar-item flex items-center gap-3 px-4 py-2 text-sm font-medium cursor-pointer transition-colors
                       ${isCuadro
                         ? isActive
                           ? 'bg-[#16a34a] text-white'
                           : 'text-white bg-[#16a34a]/20 hover:bg-[#16a34a]/30 border-l-2 border-[#16a34a]'
                         : isActive
                           ? 'bg-[#16a34a] text-white'
                           : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                       } ${collapsed ? 'justify-center' : ''}`
                    }
                    onClick={() => setMobileOpen(false)}
                    data-testid={`nav-${item.path.replace(/\//g, '') || 'dashboard'}`}
                    title={collapsed ? item.label : ''}
                  >
                    <Icon size={17} className="flex-shrink-0" />
                    {!collapsed && <span className="truncate">{item.label}</span>}
                    {!collapsed && item.badge && (
                      <span className="ml-auto bg-red-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-bold flex-shrink-0">{item.badge}</span>
                    )}
                  </NavLink>
                );
              })}
            </div>
          ))}

          {user?.role === 'admin' && (
            <>
              {!collapsed && (
                <div className="px-4 pt-4 pb-1">
                  <p className="text-[9px] text-gray-500 uppercase tracking-widest font-semibold">ADMIN</p>
                </div>
              )}
              {collapsed && <div className="h-px bg-gray-800 mx-3 my-2" />}
              <NavLink
                to="/admin"
                className={({ isActive }) =>
                  `sidebar-item flex items-center gap-3 px-4 py-2 text-sm font-medium cursor-pointer
                   ${isActive ? 'bg-[#16a34a] text-white' : 'text-gray-400 hover:bg-gray-800 hover:text-white'}
                   ${collapsed ? 'justify-center' : ''}`
                }
                data-testid="nav-admin"
                title={collapsed ? 'Administración' : ''}
              >
                <Users size={17} className="flex-shrink-0" />
                {!collapsed && <span>Administración</span>}
              </NavLink>
            </>
          )}
        </nav>

        {/* User Info & Logout */}
        <div className="border-t border-gray-800 pb-10 md:pb-0">
          {!collapsed && (
            <div className="px-4 py-3">
              <p className="text-xs text-gray-300 font-medium truncate">{user?.full_name || user?.username}</p>
              <p className="text-[10px] text-gray-500 capitalize">{user?.role === 'admin' ? 'Administrador' : 'Técnico'}</p>
            </div>
          )}
          <button
            onClick={handleLogout}
            className={`sidebar-item w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-400 hover:text-red-400 hover:bg-gray-800 transition-colors ${collapsed ? 'justify-center' : ''}`}
            data-testid="logout-btn"
          >
            <LogOut size={18} />
            {!collapsed && <span>Cerrar sesión</span>}
          </button>
          {!collapsed && (
            <div className="px-4 py-2 border-t border-gray-800">
              <p className="text-[9px] text-gray-600 text-center">Powered by Valoriza's Innovation Team</p>
            </div>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header */}
        <header className="bg-white border-b border-gray-200 px-4 md:px-6 py-3 flex items-center justify-between flex-shrink-0 gap-2">
          <div className="flex items-center gap-2 text-sm text-gray-500 min-w-0">
            <button
              onClick={() => setMobileOpen(true)}
              className="md:hidden p-1.5 -ml-1 text-gray-500 hover:text-gray-900 rounded"
              aria-label="Abrir menú"
              data-testid="open-sidebar-btn"
            >
              <Menu size={20} />
            </button>
            <ChevronRight size={14} className="hidden md:block" />
            <span className="text-gray-400 truncate">Ecoparque de Cáceres</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-500 hidden sm:block">
              {new Date().toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </span>
            <div className="w-8 h-8 rounded-full bg-[#16a34a] flex items-center justify-center text-white text-xs font-bold">
              {(user?.full_name || user?.username || 'U')[0].toUpperCase()}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="flex-1 overflow-y-auto bg-gray-50">
          {children}
        </div>
      </main>
    </div>
  );
}
